import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";

export default function EventRegistrationSuccess() {
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('event_id');
  const sessionId = searchParams.get('session_id');
  const [countdown, setCountdown] = useState(5);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const saveRegistrationAfterPayment = useCallback(async (paymentIntentId: string) => {
    if (!eventId) return;

    setSaving(true);
    setSaveError(null);

    try {
      const stored = localStorage.getItem(`event_registration_${eventId}`);
      if (!stored) {
        setSaveError(
          'Registration details were not found in your browser. Please contact the studio with your payment confirmation.'
        );
        return;
      }

      const parsed = JSON.parse(stored);
      const registrationData =
        parsed.registration_data && typeof parsed.registration_data === 'object'
          ? parsed.registration_data
          : parsed;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/events/public/${eventId}/register`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            registration_data: registrationData,
            payment_intent_id: paymentIntentId,
          }),
        }
      );

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        setSaveError(result.error || 'Failed to save your registration. Please contact the studio.');
        return;
      }

      setRegistrationComplete(true);
      localStorage.removeItem(`event_registration_${eventId}`);
    } catch (error) {
      console.error('Error saving registration:', error);
      setSaveError('Failed to save your registration. Please contact the studio.');
    } finally {
      setSaving(false);
    }
  }, [eventId]);

  const getPaymentIntentAndSaveRegistration = useCallback(async () => {
    if (!sessionId || !eventId) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stripe/get-session-payment-intent`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            session_id: sessionId,
            event_id: eventId,
          }),
        }
      );

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        setSaveError(result.error || 'Could not verify your payment. Please contact the studio.');
        return;
      }

      if (!result.payment_intent_id) {
        setSaveError('Payment was not completed. Please contact the studio if you were charged.');
        return;
      }

      await saveRegistrationAfterPayment(result.payment_intent_id);
    } catch (error) {
      console.error('Error getting payment intent:', error);
      setSaveError('Could not verify your payment. Please contact the studio.');
    }
  }, [sessionId, eventId, saveRegistrationAfterPayment]);

  useEffect(() => {
    if (sessionId && eventId) {
      getPaymentIntentAndSaveRegistration();
    }
  }, [sessionId, eventId, getPaymentIntentAndSaveRegistration]);

  useEffect(() => {
    if (!registrationComplete || saveError) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          window.location.href = `/register/event/${eventId}`;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [eventId, registrationComplete, saveError]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
        {saveError ? (
          <>
            <ExclamationTriangleIcon className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Payment Received
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Your payment went through, but we could not finalize your registration automatically.
            </p>
            <p className="text-sm text-red-600 dark:text-red-400 mb-6">{saveError}</p>
            <button
              onClick={() => getPaymentIntentAndSaveRegistration()}
              disabled={saving}
              className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 disabled:opacity-50 mb-3"
            >
              {saving ? 'Retrying…' : 'Try Again'}
            </button>
          </>
        ) : (
          <>
            <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {saving ? 'Finalizing Registration…' : 'Registration Successful!'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Thank you for registering for this event. Your payment has been processed successfully.
              {registrationComplete && (
                <span className="block mt-2 text-green-600">
                  Your registration has been saved.
                </span>
              )}
              {saving && !registrationComplete && (
                <span className="block mt-2 text-gray-500">
                  Saving your registration…
                </span>
              )}
            </p>
            {registrationComplete && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Redirecting back to event page in {countdown} seconds...
              </p>
            )}
          </>
        )}
        <button
          onClick={() => window.location.href = `/register/event/${eventId}`}
          className="mt-4 w-full bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white py-2 px-4 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
        >
          Return to Event
        </button>
      </div>
    </div>
  );
}
